import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import {
  Avatar,
  Card,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  createBrochure,
  deleteBrochureAttachment,
  getBrochureByID,
  updateBrochure,
  uploadBrochureAttachments,
} from "./services/brochure.services";
import {
  clearBrochureAttachmentID,
  clearBrochureDetails,
  setBrochureAttachmentID,
} from "./brochureSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import ShadowCard from "../../components/common/ShadowCard";
import { STATUSARRAY } from "../../_contstants/common";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { ISector } from "../sector/sectorModel";
import BrochureAttachementArray from "./BrochureAttachementArray";
import IconButton from "../../components/common/IconButton";
import { FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";

const CreateBrochure: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const BrochureID = location.state?.BrochureID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.brochure.loading);
  const BrochureDetail = useAppSelector(
    (state) => state.brochure.BrochureDetail
  );
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const [formKey, setFormKey] = useState(1);
  const [submitValue, setSubmitValue] = useState("");

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [BrochureDetail]);

  useEffect(() => {
    if (BrochureID) {
      dispatch(getBrochureByID(BrochureID));
    }
    return () => {
      dispatch(clearBrochureDetails());
    };
  }, [BrochureID]);

  useEffect(() => {
    dispatch(getAllActiveSectors());
  }, []);

  const handleSubmit = async (values: any) => {
    if (submitValue === "submit") {
      if (BrochureID) {
        try {
          const updatePayload = {
            ID: BrochureID,
            SectorID: values.SectorID ? values.SectorID : null,
            IsActive: values.IsActive === 1,
          };
          const response = await dispatch(updateBrochure(updatePayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            if (values.BrochureAttachment?.length > 0) {
              const files = new FormData();
              files.append("BrochureID", `${BrochureID}`);
              let AttachmentName: string[] = [];
              values?.BrochureAttachment?.forEach((doc: any) => {
                if (doc?.Image && typeof doc?.Image !== "string") {
                  const binaryDoc = doc?.Image[0]?.getRawFile();
                  AttachmentName.push(doc?.Title || " ");
                  files.append("attachments", binaryDoc);
                }
              });
              files.append("Title", AttachmentName.join(", "));

              if (files.get("attachments") !== null) {
                await dispatch(uploadBrochureAttachments(files));
              }
            }
            navigate("/brochure");
            dispatch(clearBrochureDetails());
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      } else {
        try {
          const insertPayload = {
            SectorID: values.SectorID ? values.SectorID : null,
            IsActive: values.IsActive === 1,
          };
          const response = await dispatch(createBrochure(insertPayload));
          if (
            response?.meta?.requestStatus === "fulfilled" &&
            response.payload?.Data?.BrochureID
          ) {
            navigate("/brochure/edit", {
              state: {
                BrochureID: +response.payload?.Data?.BrochureID,
              },
            });
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      }
    } else {
      try {
        if (values?.BrochureAttachment?.length > 0) {
          const files = new FormData();
          files.append("BrochureID", `${BrochureID}`);
          let AttachmentName: string[] = [];
          values?.BrochureAttachment?.forEach((doc: any) => {
            if (doc?.Image && typeof doc?.Image !== "string") {
              const binaryDoc = doc?.Image[0]?.getRawFile();
              AttachmentName.push(doc?.Title || " ");
              files.append("attachments", binaryDoc);
            }
          });
          files.append("Title", AttachmentName.join(", "));

          if (files.get("attachments") !== null) {
            const res = await dispatch(uploadBrochureAttachments(files));
            if (res.meta.requestStatus === "fulfilled") {
              if (BrochureID) {
                dispatch(getBrochureByID(BrochureID));
              }
            }
          }
        }
        // navigate("/brochure");
        // dispatch(clearBrochureDetails());
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={BrochureDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {BrochureID ? "Update Brochure" : "Add Brochure"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"SectorID"}
                    name={"SectorID"}
                    label={"Sector"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={SectorList?.map((sector: ISector) => {
                      return {
                        value: sector?.ID,
                        label: sector?.SectorName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    id={"IsActive"}
                    name={"IsActive"}
                    label={"Status"}
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    textAlign: "end",
                    marginTop: 35,
                    width: "100%",
                  }}
                >
                  <Button
                    type="submit"
                    fillMode={"solid"}
                    themeColor={"primary"}
                    onClick={() => setSubmitValue("submit")}
                    disabled={!formRenderProps.allowSubmit || loading}
                  >
                    {BrochureID ? "Update" : "Save"}
                  </Button>

                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/brochure")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            {BrochureID && (
              <BrochureAttachmentComponent
                formRenderProps={formRenderProps}
                setSubmitValue={setSubmitValue}
              />
            )}
          </FormElement>
        )}
      />
    </>
  );
};

const BrochureAttachmentComponent = ({
  formRenderProps,
  setSubmitValue,
}: {
  formRenderProps: FormRenderProps;
  setSubmitValue: any;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const location = useLocation();
  // const BrochureID = location.state?.BrochureID;
  const loading = useAppSelector((state) => state.brochure.loading);
  const BrochureDetail = useAppSelector(
    (state) => state.brochure.BrochureDetail
  );

  const handleOpenAttachmentDeleteDialog = (ID: number) => {
    dispatch(openDialog("deleteBrochureAttachment"));
    dispatch(setBrochureAttachmentID(ID));
  };

  // const handleSubmit = async (formRenderProps: FormRenderProps) => {
  //   const BrochureAttachment =
  //     formRenderProps.valueGetter("BrochureAttachment");
  // };

  return (
    <>
      <DeleteBrochureAttachmentDialog />
      <ShadowCard style={{ padding: 12, marginBottom: 10, marginTop: 10 }}>
        <GridLayout
          style={{ marginRight: 20 }}
          gap={{ rows: 0, cols: 10 }}
          cols={[{ width: "33.33%" }, { width: "33.33%" }, { width: "33.33%" }]}
        >
          <GridLayoutItem colSpan={3}>
            <Typography.h4>Brochure Attachments</Typography.h4>
          </GridLayoutItem>
          <GridLayoutItem colSpan={3}>
            <FieldArray
              formRenderProps={formRenderProps}
              component={BrochureAttachementArray}
              name={"BrochureAttachment"}
            />
          </GridLayoutItem>
          <GridLayoutItem
            colSpan={3}
            style={{
              textAlign: "end",
              marginTop: 20,
              width: "100%",
            }}
          >
            <Button
              type="submit"
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={() => setSubmitValue("imageUpload")}
              disabled={!formRenderProps.allowSubmit || loading}
            >
              Save Brochures
            </Button>

            <Button
              type="button"
              fillMode={"outline"}
              themeColor={"primary"}
              style={{ marginLeft: 10 }}
              onClick={() => navigate("/brochure")}
            >
              Cancel
            </Button>
          </GridLayoutItem>
          {BrochureDetail?.Documents &&
            BrochureDetail?.Documents?.length > 0 && (
              <>
                <GridLayoutItem colSpan={3}>
                  <hr />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Typography.h5 className="mb-1">Attachments</Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    marginTop: 10,
                  }}
                >
                  {BrochureDetail?.Documents?.map((image: any) => (
                    <Card
                      style={{
                        padding: 10,
                        maxWidth: 600,
                        minWidth: 300,
                      }}
                    >
                      <div className="d-flex gap-2 align-items-center">
                        <Avatar
                          style={{
                            width: 60,
                            height: 60,
                            flexBasis: 60,
                            backgroundColor: "transparent",
                          }}
                          rounded={"full"}
                          type={"image"}
                        >
                          <img
                            style={{ width: 60, height: 60 }}
                            src={image?.FileName}
                            alt={"Attachment"}
                          />
                        </Avatar>
                        <div>
                          <p className="mb-1">{image?.Title}</p>
                          <div className="d-flex gap-2">
                            <IconButton
                              type="button"
                              fillMode="solid"
                              size="small"
                              title="View"
                              onClick={() => window.open(image?.FileName)}
                              icon={FaEye}
                              iconClassName="absolute-position"
                              iconStyle={{ fontSize: "20px" }}
                            />
                            <IconButton
                              type="button"
                              fillMode="solid"
                              size="small"
                              title="Delete"
                              themeColor="error"
                              onClick={() =>
                                handleOpenAttachmentDeleteDialog(image?.ID)
                              }
                              icon={MdDelete}
                              iconClassName="absolute-position"
                              iconStyle={{ fontSize: "20px" }}
                            ></IconButton>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </GridLayoutItem>
              </>
            )}
        </GridLayout>
      </ShadowCard>
    </>
  );
};

const DeleteBrochureAttachmentDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const BrochureID = location.state?.BrochureID;
  const BrochureAttachmentID = useAppSelector(
    (state) => state.brochure.BrochureAttachmentID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ReportID: number | null) => {
    if (ReportID) {
      const response = await dispatch(deleteBrochureAttachment(ReportID));
      if (response.meta.requestStatus === "fulfilled") {
        if (BrochureID) {
          dispatch(getBrochureByID(BrochureID));
        }
        dispatch(closeDialog());
        dispatch(clearBrochureAttachmentID());
      } else {
        if (BrochureID) {
          dispatch(getBrochureByID(BrochureID));
        }
        dispatch(closeDialog());
        dispatch(clearBrochureAttachmentID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteBrochureAttachment" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Attachment?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this attachment?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    handleDeleteAction(BrochureAttachmentID);
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateBrochure;
